import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const EMPTY = { name: "", description: "", image_url: "", active: true, type: "machine" };

export default function AdminCategories() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [tab, setTab] = useState("machine");
  const [form, setForm] = useState(EMPTY);

  const load = () => { setLoading(true); api.get("/categories?all=true").then((r) => setCats(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditId(null); setForm({ ...EMPTY, type: tab }); setOpen(true); };
  const openEdit = (c) => { setEditId(c.id); setForm({ name: c.name, description: c.description || "", image_url: c.image_url || "", active: c.active, type: c.type || "machine" }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    try {
      if (editId) await api.put(`/admin/categories/${editId}`, form);
      else await api.post("/admin/categories", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/categories/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const shown = cats.filter((c) => (c.type || "machine") === tab);

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-extrabold text-slate-900">{tab === "spare" ? "Spare Part Categories" : "Machine Categories"}</h1>
        <button data-testid="add-category-btn" onClick={openNew} className="inline-flex items-center gap-1.5 rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white"><Plus size={16} /> Add Category</button>
      </div>

      <div className="mt-4 inline-flex rounded-full border border-slate-200 bg-white p-1">
        {[["machine", "Machine Categories"], ["spare", "Spare Part Categories"]].map(([v, l]) => (
          <button key={v} data-testid={`cat-tab-${v}`} onClick={() => setTab(v)}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold ${tab === v ? "bg-brand text-white" : "text-slate-600"}`}>{l}</button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
        <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((c) => (
            <div key={c.id} data-testid={`category-item-${c.slug}`} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3">
              <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-slate-100">{c.image_url && <img src={c.image_url} alt="" className="h-full w-full object-cover" />}</div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-slate-900">{c.name}</p>
                <p className="text-xs text-slate-500">{c.machine_count} machines {!c.active && "• Inactive"}</p>
              </div>
              <button onClick={() => openEdit(c)} className="rounded-lg border border-slate-300 p-2 text-slate-600"><Edit size={15} /></button>
              <button onClick={() => remove(c.id)} className="rounded-lg border border-red-200 p-2 text-red-500"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editId ? "Edit" : "Add"} Category</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Type</Label>
              <div className="mt-1 flex gap-2">
                {[["machine", "Machine Category"], ["spare", "Spare Part Category"]].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setForm({ ...form, type: v })}
                    className={`flex-1 rounded-lg border px-3 py-2 text-xs font-semibold ${form.type === v ? "border-[hsl(var(--brand))] bg-[hsl(var(--brand))] text-white" : "border-slate-300 text-slate-600"}`}>{l}</button>
                ))}
              </div>
            </div>
            <div><Label>Name</Label><Input data-testid="cat-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Description</Label><Textarea data-testid="cat-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div><Label>Image URL</Label><Input data-testid="cat-image" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." /></div>
            <div className="flex items-center gap-2"><Switch data-testid="cat-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Active</Label></div>
            <button data-testid="cat-save" onClick={save} className="w-full rounded-full bg-brand py-2.5 font-bold text-white">Save</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
